import { useRecordContext } from 'react-admin';
import { JsonField } from 'react-admin-json-view';

import { GetB2bCampaignsPayoutsOneResponse } from '@Common/types/b2b/campaigns';

import { contractCampaignActionHandlerChoices } from '@sh4res/bo-common/choices/contracts';
import { b2bPayoutsStatusChoices } from '@sh4res/bo-common/choices/payouts';
import { b2bPaymentStatusChoices } from '@sh4res/bo-common/choices/withdrawals';
import { ShowFields, ShowSection, ShowWithNavigation } from '@sh4res/bo-common/components';
import { PayoutTransferIdField } from '@sh4res/bo-common/components/B2bCampaigns/payouts/PayoutTransferIdField';
import { CurrencyField, IdField, SelectChipField, UTCDateField } from '@sh4res/bo-common/fields';
import { useResourceBasePath } from '@sh4res/bo-common/hooks';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

const testId = 'BoExternalB2bCampaignPayoutsShow';

const B2bCampaignPayoutsDetails = () => {
  const resourceBasePath = useResourceBasePath();
  const record = useRecordContext<GetB2bCampaignsPayoutsOneResponse>();
  const showTime = !isBoExternal();

  if (!record) {
    return null;
  }

  return (
    <ShowSection label={`resources.${resourceBasePath}campaigns/payouts.sections.title`}>
      <ShowFields<GetB2bCampaignsPayoutsOneResponse>
        fields={{
          id: (
            <IdField
              reference={`${resourceBasePath}campaigns/payouts`}
              data-testid={`${testId}_IdField`}
            />
          ),
          b2b_campaign_id: (
            <IdField
              reference={`${resourceBasePath}campaigns`}
              data-testid={`${testId}_CampaignIdField`}
            />
          ),
          savings_funds_request_id: (
            <IdField
              reference={`${resourceBasePath}savings_funds_requests`}
              data-testid={`${testId}_SavingsFundsRequestIdField`}
            />
          ),
          b2b_employee_id: (
            <IdField
              reference={`${resourceBasePath}employees`}
              data-testid={`${testId}_EmployeeIdField`}
            />
          ),
          status: (
            <SelectChipField
              choices={b2bPayoutsStatusChoices}
              data-testid={`${testId}_StatusField`}
            />
          ),
          payout_by: (
            <SelectChipField
              choices={contractCampaignActionHandlerChoices}
              data-testid={`${testId}_PayoutByField`}
            />
          ),
          transfer_id: <PayoutTransferIdField data-testid={`${testId}_TransferIdField`} />,
          transfer_mdlr_status: (
            <SelectChipField
              choices={b2bPaymentStatusChoices}
              displayRawValueIfChoiceNotFound
              data-testid={`${testId}_TransferMdlrStatusField`}
            />
          ),
          transfer_amount: (
            <CurrencyField currency="user_currency" testId={`${testId}_TransferAmountField`} />
          ),
          transfer_created_at: (
            <UTCDateField showTime={showTime} data-testid={`${testId}_TransferCreatedAtField`} />
          ),
          transfer_executed_at: (
            <UTCDateField showTime={showTime} data-testid={`${testId}_TransferExecutedAtField`} />
          ),
          total_gross_amount: (
            <CurrencyField currency="user_currency" testId={`${testId}_TotalGrossAmountField`} />
          ),
          user_currency_amount: (
            <CurrencyField currency="user_currency" testId={`${testId}_UserCurrencyAmountField`} />
          ),
          tax_amount: (
            <CurrencyField currency="user_currency" testId={`${testId}_TaxAmountField`} />
          ),
          tax_info: <JsonField source="tax_info" data-testid={`${testId}_TaxInfoField`} />,
          created_at: <UTCDateField showTime={showTime} data-testid={`${testId}_CreatedAtField`} />,
          updated_at: <UTCDateField showTime={showTime} data-testid={`${testId}_UpdatedAtField`} />,
        }}
      />
    </ShowSection>
  );
};

export const B2bCampaignPayoutsShow = () => (
  <ShowWithNavigation actions={false}>
    <B2bCampaignPayoutsDetails />
  </ShowWithNavigation>
);
